// IndexedDBImageService.js
import { onMounted, onBeforeUnmount, onBeforeMount } from 'vue'; // 引入 Vue 的生命週期鉤子
import { openDB } from 'idb'; // 導入 idb 庫，並引入 IDBKeyRange

// --- IndexedDB 配置 ---
const DB_NAME = 'InnoluxBenefit';
let STORE_NAME = '';
const DB_VERSION = 2; // 將資料庫版本從 1 升級到 2

// 清理相關配置
const DEFAULT_EXPIRATION_MINUTES = 60 * 24; // 圖片預設保存時間為 24 小時 (1440 分鐘)，如果沒有指定
const CLEANUP_INTERVAL_MS = 10 * 60 * 1000; // 每 5 分鐘執行一次圖片清理

let dbPromise = null;
let dbInstance = null; // 儲存 IndexedDB 資料庫實例，確保它是單例
let cleanupIntervalId = null; // 用於儲存 setInterval 的 ID，以便在組件卸載時清除

const r = {}; // 我們的接口物件

// --- 私有輔助函數：初始化 IndexedDB ---
const _initDB = async () => {
  if (dbInstance) {
    return dbInstance; // 如果已初始化，直接返回現有實例
  }
  try {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // 版本 1 的升級邏輯 (創建 object store)
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            console.log(`IndexedDB object store '${STORE_NAME}' created.`);
          }
        }
        // 版本 2 的升級邏輯 (添加 expiresAt 索引)
        if (oldVersion < 2) {
          const store = transaction.objectStore(STORE_NAME);
          if (!store.indexNames.contains('expiresAt')) {
            store.createIndex('expiresAt', 'expiresAt', { unique: false, multiEntry: false });
            console.log(`IndexedDB index 'expiresAt' created for '${STORE_NAME}'.`);
          }
        }
      },
    });
    dbInstance = await dbPromise;
    return dbInstance;
  } catch (error) {
    console.error('Failed to initialize IndexedDB:', error);
    throw new Error('資料庫初始化失敗');
  }
};

const _checkDbReadyAsync = async () => {
  if (!dbInstance) {
    dbInstance = await dbPromise;
  }
  return dbInstance;
};

const _cleanupExpired = async () => {
  const db = await _checkDbReadyAsync();

  try {
    // 開啟一個讀寫事務
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('expiresAt');
    const now = Date.now();

    const range = IDBKeyRange.upperBound(now);

    const list = await index.getAll(range);
    if (list != null && list.length > 0) {
      for (let i = 0; i < list.length; i++) {
        await _delete(list[i].id);
      }
    }
  } catch (error) {
    console.error('清理失敗:', error);
  }
};


r.set = async (id, data, expirationMinutes = DEFAULT_EXPIRATION_MINUTES) => {
  const db = await _checkDbReadyAsync();

  const now = Date.now(); // 當前時間戳
  let expiresAt = null; // 預設為永不過期

  if (expirationMinutes && expirationMinutes > 0) {
    expiresAt = now + expirationMinutes * 60 * 1000; // 計算過期時間戳
  }

  try {
    await db.put(STORE_NAME, {
      id: id,
      data: data,
      timestamp: now, // 圖片儲存時間
      expiresAt: expiresAt, // 圖片過期時間
    });
  } catch (error) {
    console.error(`儲存圖片 '${id}' 失敗:`, error);
  }
};

r.get = async (id) => {
  const db = await _checkDbReadyAsync();

  if (!id) {
    throw new Error('請提供要載入的圖片 ID。');
  }

  try {
    const item = await db.get(STORE_NAME, id);
    if (item && item.data) {
      return item.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`'${id}' 失敗:`, error);
  }
};

const _delete = async (id) => {
  const db = await _checkDbReadyAsync();

  if (!id) {
    throw new Error('請提供ID。');
  }

  try {
    await db.delete(STORE_NAME, id);
  } catch (error) {
    console.error(`'${id}' 失敗:`, error);
  }
};
r.delete = _delete;

r.getAllKeys = async () => {
  const db = await _checkDbReadyAsync();

  try {
    return await db.getAllKeys(STORE_NAME);
  } catch (error) {
    console.error('取得所有失敗:', error);
  }
};

r.clearAll = async () => {
  const db = await _checkDbReadyAsync();

  try {
    await db.clear(STORE_NAME);
  } catch (error) {
    console.error('清空所有失敗:', error);
  }
};

/**
 * (可選) 取得所有過期圖片的 ID 列表 (僅供檢查，自動清理會處理)
 * @returns {Promise<string[]>} 返回一個包含所有過期圖片 ID 的陣列。
 */
const textCursor = async () => {
  const db = await _checkDbReadyAsync();
  try {
    const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);
    const index = store.index('expiresAt');
    const now = Date.now();
    const range = IDBKeyRange.upperBound(now, true); // 所有 expiresAt <= now 的項目

    let request = await index.getAll(range);
    await r.delete(request[0].id);

  } catch (error) {
    console.error('載入 ID 失敗:', error);
    throw error; // 重新拋出錯誤，讓上層捕獲
  }
}

export function useService(storeName) {
  STORE_NAME = storeName;

  onBeforeMount(async () => {
    try {
      await _initDB();

      cleanupIntervalId = setInterval(_cleanupExpired, CLEANUP_INTERVAL_MS);
      _cleanupExpired();
    } catch (e) {
      // 可以在這裡處理初始化失敗的邏輯，例如設定一個錯誤狀態
      console.error("IndexedDB 初始化失敗或清理排程啟動失敗。", e);
    }
  });
  return r; // 返回包含所有方法的物件
}